"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Download } from "lucide-react";
import Image from "next/image";

interface Device {
  name: string;
  icon?: React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>;
  logo?: string;
  type: "app" | "extension";
  color?: string;
}

interface DeviceDownloadGridProps {
  title: string;
  description: string;
  devices: Device[];
  footnote: string;
  footnoteLink: string;
}

const DeviceDownloadGrid = ({
  title,
  description,
  devices,
  footnote,
  footnoteLink,
}: DeviceDownloadGridProps) => {
  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-6xl mx-auto flex flex-col items-center gap-16">
        
        {/* Header */}
        <div className="text-center space-y-6 max-w-3xl">
          <BlurFade delay={0.1}>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              {title}
            </h2>
          </BlurFade>
          <BlurFade delay={0.2}>
            <p className="text-base text-foreground/60 leading-relaxed font-medium">
              {description}
            </p>
          </BlurFade>
        </div>

        {/* Grid - All apps in single row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 w-full max-w-5xl">
          {devices.map((device, i) => {
            return (
              <BlurFade key={i} delay={0.3 + i * 0.05}>
                <div className="group bg-white p-6 rounded-2xl border border-border hover:border-primary hover:shadow-lg transition-all cursor-pointer flex flex-col items-center text-center gap-4">
                  <div className="w-16 h-16 flex items-center justify-center">
                    {device.logo ? (
                      <Image
                        src={device.logo}
                        alt={`${device.name} logo`}
                        width={64}
                        height={64}
                        className="w-16 h-16 object-contain"
                      />
                    ) : device.icon ? (
                      <device.icon
                        size={48}
                        className="text-muted-foreground"
                        style={{ color: device.color }}
                      />
                    ) : null}
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-foreground">
                      {device.name}
                    </h3>
                  </div>
                  <div className="mt-auto">
                    <div className="w-8 h-8 rounded-full bg-foreground flex items-center justify-center text-white group-hover:bg-primary transition-colors">
                      <Download size={16} />
                    </div>
                  </div>
                </div>
              </BlurFade>
            );
          })}
        </div>

        {/* Footer Note */}
        <BlurFade delay={0.8}>
          <p className="text-[11px] font-medium text-foreground/40">
            {footnote.split("Windows ARM-based processor.")[0]}
            <a
              href={footnoteLink}
              className="underline hover:text-primary transition-colors"
            >
              Windows ARM-based processor.
            </a>
          </p>
        </BlurFade>
      </div>
    </section>
  );
};

export default DeviceDownloadGrid;





